import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Grid, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";

import QueryData from "Constants/QueryData";
import { STATIONS } from "Constants/Stations";
import { IStation } from "Types/station";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import useStations from "Hooks/useStations";

import { changeStationsFilter } from "Redux/filtersSlice";
import { setMapData } from "Redux/mapSlice";
import { changeMenu } from "Redux/menuSlice";
import { changeModal } from "Redux/modalSlice";
import { setStation } from "Redux/singleLocationSlice";

import Api from "Api";

import ConfirmModal from "Components/ConfirmModal";
import DeleteStationModal from "../Modals/DeleteStationModal";

import Header from "./Header";
import Table, { StyledTableCell } from "./Table";

export default function Stations() {
  const dispatch = useAppDispatch();
  const { stations } = useAppSelector((state) => state.stations);
  const { name: mode } = useAppSelector((state) => state.operation);

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [stationId, setStationId] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [stationPaths, setStationPaths] = useState<any[]>([]);

  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isFetching } = useStations();

  const finalData = mode === "edit-path" ? stations : data;

  const deleteStation = useMutation({
    mutationFn: () => {
      return Api.delete(`path/station/${stationId}`).then((res) => {
        toast.success("ایستگاه با موفقیت حذف شد");
        setShowConfirm(false);
        setOpen(false);
        queryClient.invalidateQueries({ queryKey: [QueryData.STATION.key] });
      });
    },
  });

  const showOnMap = (station: IStation) => {
    dispatch(changeMenu("disable"));
    dispatch(setStation([station]));
    dispatch(setMapData({ view: { center: { lat: station.lat, lng: station.lng }, zoom: 14 } }));
  };
  const goForEdit = (station: IStation) => {
    dispatch(changeModal({ status: "add-station", data: { ...station, isEdit: true } }));
  };
  const goForDelete = (id: number) => {
    Api.get(`path/station/${id}`).then((res: any) => {
      const pathsLength = res.data.Data.station.paths.length;
      setStationId(id);
      if (pathsLength < 2) {
        setShowConfirm(true);
      } else {
        setOpen(true);
        setStationPaths(res.data.Data.station.paths);
      }
    });
  };
  const rowContent = (_index: number) => {
    if (!finalData) return;

    const station = finalData[_index];
    const stationData = STATIONS.filter((i) => i.type === station.type)[0];

    return (
      <>
        <StyledTableCell className="fa-number">{_index + 1}</StyledTableCell>
        <StyledTableCell>{station.title}</StyledTableCell>
        <StyledTableCell>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: "center" }}>
            {stationData.icon}
            <Typography>{stationData.text}</Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <IconButton color="primary" onClick={() => showOnMap(station)}>
            <LocationOnIcon />
          </IconButton>
        </StyledTableCell>
        <StyledTableCell>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
            <IconButton color="black" onClick={() => goForEdit(station)}>
              <EditIcon />
            </IconButton>
            <IconButton color="error" onClick={() => goForDelete(station.id)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </StyledTableCell>
      </>
    );
  };
  const handleChangeFilters = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    dispatch(changeStationsFilter({ [name]: value }));
  };

  return (
    <>
      <ConfirmModal
        open={showConfirm}
        setOpen={setShowConfirm}
        message="ایستگاه انتخاب شده حذف خواهد شد."
        mutation={deleteStation}
      />
      <DeleteStationModal isOpen={open} setOpen={setOpen} stationPaths={stationPaths} mutation={deleteStation} isList={true} />

      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Header>
          <Stack sx={{ flexGrow: 1 }} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h4" sx={{ fontWeight: "800" }}>
              لیست ایستگاه ها
            </Typography>

            <Typography sx={{ fontWeight: "500" }} className="fa-number">
              تعداد ایستگاه‌ها: {finalData?.length || 0}
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button variant="white" size="large">
                <LocalPrintshopOutlinedIcon />
              </Button>
              <Button variant="white" size="large">
                <SystemUpdateAltOutlinedIcon />
              </Button>
            </Stack>
          </Stack>
        </Header>

        <Grid container spacing={1} mb={2}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth label="نام ایستگاه" name="q" onChange={handleChangeFilters} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth label="نوع ایستگاه" select name="type" onChange={handleChangeFilters}>
              <MenuItem value="">همه</MenuItem>
              {STATIONS.map((station) => (
                <MenuItem value={station.type}>{station.text}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1}>
            <LoadingButton loading={isFetching} fullWidth variant="contained" size="large" onClick={() => refetch()}>
              فیلتر
            </LoadingButton>
          </Grid>
        </Grid>

        <Table
          headerColumns={[
            { title: "ردیف", style: {} },
            { title: "نام ایستگاه", style: {} },
            { title: "نوع ایستگاه", style: {} },
            { title: "موقعیت مکانی", style: {} },
            { title: "عملیات", style: {} },
          ]}
          isLoading={isLoading}
          data={finalData}
          rowContent={rowContent}
        />
      </Box>
    </>
  );
}
